import {CENTER_MESSAGE_TYPES} from '../Constants';
import * as ActionTypes from './actionTypes';
import {populateAqfxDataAsync} from './populate_aqfx_data';
import {populateAwsDataAsync, populateAwsDataForGraphAsyncPromise} from './populate_aws_data';
import {activateBoundary, toggleBoundary} from './populate_boundary_details';
import {populateBurnPrescriptionsAsync} from './populate_burn_prescriptions';
import {populateFmsDataAsync, populateFmsDataForGraphAsyncPromise} from './populate_fms_data';
import {populateIndicatorDetailsAsyncPromise} from './populate_indicator_details';
import {populateManualWeatherAsync} from './populate_manual_weather';
import {mapRecentred, setMapBoundsAndZoom, setMapCenter} from './populate_map_details';
import {
  populateWindDirectionDataPromise,
  populateWindMagnitudeDataPromise,
  setWindBarbData,
  setWindVelocityData
} from './populate_wind_data';
import {updateIndicatorTimesAsyncPromise} from './update_indicator_times';

// src_location_change indicates if the source of the action was a URL Location Change
export const setActivePoint = (lat, lng, open_popup = true, src_location_change = false) => {
  return {
    type: ActionTypes.SET_ACTIVE_POINT,
    latlng: { lat, lng },
    open_popup,
    src_location_change
  };
};

export const setCursorPoint = (lat, lng, open_popup = false, src_location_change = false) => {
  return {
    type: ActionTypes.SET_CURSOR_POINT,
    latlng: { lat, lng },
    open_popup,
    src_location_change
  };
};

export const setActiveIncidentFeature = (feature) => {
  return {
    type: ActionTypes.SET_ACTIVE_INCIDENT_FEATURE,
    latlng: { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] },
    eventId: feature.eventId,
    prescriptionId: feature.prescriptionId
  };
};

export const setActiveIncident = (eventId) => {
  return { type: ActionTypes.SET_ACTIVE_INCIDENT, eventId };
};

export const setActivePrescription = (prescriptionId, src_location_change) => {
  return { type: ActionTypes.SET_ACTIVE_PRESCRIPTION, prescriptionId, src_location_change };
};

export const setActivateIncidentWhenLoaded = (eventId) => {
  return { type: ActionTypes.ACTIVATE_INCIDENT_WHEN_LOADED, eventId };
};

export const setAppMode = (app_mode, src_location_change = false) => {
  return {
    type: ActionTypes.SET_APP_MODE,
    app_mode,
    src_location_change
  };
};

export const setQsStationId = (station_id) => {
  return {
    type: ActionTypes.SET_QS_STATION_ID,
    station_id: station_id ? parseInt(station_id, 10) : undefined
  };
};

export const toggleOverlay = (id) => {
  return {
    type: ActionTypes.TOGGLE_OVERLAY,
    id
  };
};

export const toggleIncidentType = (id) => {
  return {
    type: ActionTypes.TOGGLE_INCIDENT_TYPE,
    id
  };
};

export const activateOverlay = (id) => {
  return {
    type: ActionTypes.ACTIVATE_OVERLAY,
    id
  };
};

export const setUrl = () => {
  return {
    type: ActionTypes.SET_URL
  };
};

export const setUrlHash = (url_hash) => {
  return {
    type: ActionTypes.SET_URL_HASH,
    url_hash
  };
};

export const startLocationSearch = () => {
  return { type: ActionTypes.START_LOCATION_SEARCH };
};

export const stopLocationSearch = () => {
  return { type: ActionTypes.STOP_LOCATION_SEARCH };
};

// src_location_change indicates if the source of the action was a URL Location Change
export const switchIndicators = (indicator_prefix, src_location_change = false) => {
  return {
    type: ActionTypes.SWITCH_ACTIVE_INDICATOR,
    indicator_prefix,
    src_location_change
  };
};

export const closePopup = () => {
  return { type: ActionTypes.CLOSE_POPUP };
};

export const openGraphsView = (src_location_change = false) => {
  return { type: ActionTypes.OPEN_GRAPHS_VIEW, src_location_change };
};

export const closeGraphsView = () => {
  return { type: ActionTypes.CLOSE_GRAPHS_VIEW };
};

export const ejectGraphsView = () => {
  return { type: ActionTypes.EJECT_GRAPHS_VIEW };
};

export const printGraphsView = () => {
  return { type: ActionTypes.PRINT_GRAPHS_VIEW };
};

export const toggleGraphsExpanded = (src_location_change = false) => {
  return { type: ActionTypes.TOGGLE_GRAPHS_EXPANDED, src_location_change };
};

export const setGraphsExpanded = (expanded, src_location_change) => {
  return { type: ActionTypes.SET_GRAPHS_EXPANDED, data: expanded, src_location_change };
};

export const setPopOut = (print_on_load) => {
  return { type: ActionTypes.SET_POP_OUT, print_on_load };
};

export const selectDataSet = (data_set) => {
  return { type: ActionTypes.SELECT_DATA_SET, data: { data_set } };
};

export const toggleGraphsHistory = () => {
  return { type: ActionTypes.TOGGLE_GRAPHS_HISTORY };
};

export const toggleGraphsDays = () => {
  return { type: ActionTypes.TOGGLE_GRAPHS_DAYS };
};

export const toggleShowAllCalcColumns = () => {
  return { type: ActionTypes.TOGGLE_SHOW_ALL_CALC_COLUMNS };
};

export const setTableBackground = (table_background) => {
  return { type: ActionTypes.SET_TABLE_BACKGROUND, table_background };
};

export const setShowWeather = (show_weather) => {
  return { type: ActionTypes.SET_SHOW_WEATHER, show_weather };
};

export const setTableDirections = (table_directions) => {
  return { type: ActionTypes.SET_TABLE_DIRECTIONS, table_directions };
};

export const togglePointDataDisplay = () => {
  return { type: ActionTypes.TOGGLE_POINT_DATA_DISPLAY };
};

export const togglePopupExpanded = () => {
  return { type: ActionTypes.TOGGLE_POPUP_EXPANDED };
};

export const dropdownLegendOpen = (open) => {
  return { type: ActionTypes.DROPDOWN_LEGEND_OPEN, open };
};

export const dropdownBoundariesOpen = (open) => {
  return { type: ActionTypes.DROPDOWN_BOUNDARIES_OPEN, open };
};

export const dropdownIndicatorsOpen = (open) => {
  return { type: ActionTypes.DROPDOWN_INDICATORS_OPEN, open };
};

export const setActiveTime = (selected_time) => {
  return {
    type: ActionTypes.SET_ACTIVE_TIME,
    selected_time
  };
};

export const setCentreMessage = (body, heading, dangerouslySetInnerHTML) => {
  const type = CENTER_MESSAGE_TYPES.INFO;
  return { type: ActionTypes.SET_CENTRE_MESSAGE, message: { body, heading, type, dangerouslySetInnerHTML } };
};

export const setCentreWarningMessage = (body, dangerouslySetInnerHTML) => {
  const heading = 'Warning';
  const type = CENTER_MESSAGE_TYPES.WARNING;
  return { type: ActionTypes.SET_CENTRE_MESSAGE, message: { body, heading, type, dangerouslySetInnerHTML } };
};

export const closeCentreMessage = () => {
  return { type: ActionTypes.SET_CENTRE_MESSAGE, message: null };
};

export const setLoadingMessage = (message) => {
  return { type: ActionTypes.SET_LOADING_MESSAGE, message: message };
};

export const clearLoadingMessage = () => {
  return { type: ActionTypes.SET_LOADING_MESSAGE, message: null };
};

export const setDisplayTime = (display_time) => {
  return {
    type: ActionTypes.SET_DISPLAY_TIME,
    display_time
  };
};

export const setDisplayTimeForActiveIndicator = (selected_time) => {
  return {
    type: ActionTypes.SET_DISPLAY_TIME_FOR_ACTIVE_INDICATOR,
    selected_time
  };
};

export const resetIndicatorOverlays = () => {
  return { type: ActionTypes.RESET_INDICATOR_OVERLAYS };
};

export const populateValidTimes = (from_time) => {
  return {
    type: ActionTypes.POPULATE_VALID_TIMES,
    from_time
  };
};

export const togglePlaying = () => {
  return (dispatch, getState) => dispatch({
    type: getState().play.playing ?
          ActionTypes.STOP_PLAYING :
          ActionTypes.START_PLAYING
  });
};

export const setPlaySpeed = (speed) => {
  return { type: ActionTypes.SET_PLAY_SPEED, speed };
};

export const stopPlaying = () => {
  return { type: ActionTypes.STOP_PLAYING };
};

export const addLoadedTimes = (times) => {
  return { type: ActionTypes.ADD_LOADED_TIMES, times };
};

export const clearLoadedTimes = () => {
  return { type: ActionTypes.CLEAR_LOADED_TIMES };
};

export const incrementLoadingIndex = () => {
  return { type: ActionTypes.INCREMENT_LOADING_INDEX };
};

export const resetLoadingIndex = () => {
  return { type: ActionTypes.RESET_LOADING_INDEX };
};

export const setIndicatorOverlayIntervals = (intervals) => {
  return { type: ActionTypes.SET_INDICATOR_OVERLAY_INTERVALS, intervals };
};

export const clearIndicatorOverlayIntervals = () => {
  return { type: ActionTypes.CLEAR_INDICATOR_OVERLAY_INTERVALS };
};

export const extractIndicatorOverlayIntervals = () => {
  return { type: ActionTypes.EXTRACT_INDICATOR_OVERLAY_INTERVALS };
};

export const toggleMenu = (menu_name) => {
  return {
    type: ActionTypes.TOGGLE_MENU,
    menu_name
  };
};

export const error = (message) => {
  console.log(`ERROR: ${message}`);
  console.trace();
  return (dispatch) => _openMessage(dispatch, message, 'ERROR');
};

export const warning = (message) => {
  return (dispatch) => _openMessage(dispatch, message, 'WARNING');
};

export const info = (message) => {
  return (dispatch) => _openMessage(dispatch, message, 'INFO');
};

export const updatePointDetails = () => {
  return { type: ActionTypes.UPDATE_POINT_DETAILS };
};

export const startTimelineDrag = () => {
  return { type: ActionTypes.START_TIMELINE_DRAG };
};

export const stopTimelineDrag = () => {
  return { type: ActionTypes.STOP_TIMELINE_DRAG };
};

export const toggleTimelineHistory = () => {
  return { type: ActionTypes.TOGGLE_TIMELINE_HISTORY };
};

export const setTimelineToNow = () => {
  return { type: ActionTypes.SET_TIMELINE_NOW };
};

export const resetMap = () => {
  return { type: ActionTypes.RESET_MAP };
};

export const resetLayers = () => {
  return { type: ActionTypes.RESET_LAYERS };
};

export const toggleDisclaimer = () => {
  return { type: ActionTypes.TOGGLE_DISCLAIMER };
};

export const toggleAqfxPanel = () => {
  return { type: ActionTypes.TOGGLE_AQFX_PANEL };
};

export const selectAqfxNumber = (num) => {
  return {
    type: ActionTypes.SELECT_AQFX_NUMBER,
    selected_number: num
  };
};

export const windDataLoadStarting = () => {
  return { type: ActionTypes.WIND_DATA_LOAD_STARTING };
};

export const windDataLoadComplete = () => {
  return { type: ActionTypes.WIND_DATA_LOAD_COMPLETE };
};

export const clearWindData = () => {
  return { type: ActionTypes.CLEAR_WIND_DATA };
};

export const setBehaviourVariables = (data_set, variables) => {
  return {
    type: ActionTypes.SET_BEHAVIOUR_VARIABLES,
    data_set,
    variables
  };
};

export const setManualWeatherData = (meta_data, original_data, new_data) => {
  const filtered_keys = meta_data.filter(m => m.editable)
                                 .filter(m => typeof new_data[m.key] !==
                                              'undefined' &&
                                              new_data[m.key] !==
                                              original_data[m.key])
                                 .map(m => m.key);

  const data = {};
  filtered_keys.forEach(key => {data[key] = new_data[key];});

  return {
    type: ActionTypes.SET_MANUAL_WEATHER_DATA,
    time: original_data.name,
    data
  };
};

export const setManualWeatherDataSingleVariable = (indicator_key, new_data) => {
  return {
    type: ActionTypes.SET_MANUAL_WEATHER_DATA_SINGLE_VARIABLE,
    indicator_key,
    value: new_data[indicator_key]
  };
};

export const resetManualWeatherData = (original_data) => {
  return {
    type: ActionTypes.RESET_MANUAL_WEATHER_DATA,
    time: original_data.name
  };
};

export const resetManualWeatherDataSingleVariable = (indicator_key) => {
  return {
    type: ActionTypes.RESET_MANUAL_WEATHER_DATA_SINGLE_VARIABLE,
    indicator_key
  };
};

export const resetAllManualWeatherData = () => {
  return {
    type: ActionTypes.RESET_ALL_MANUAL_WEATHER_DATA
  };
};

export const exportData = (format, include_manual, starting_day, days_lasting) => {
  return {
    type: ActionTypes.EXPORT_DATA,
    format,
    include_manual,
    starting_day,
    days_lasting
  };
};

export const storageEvent = (event) => {
  return {
    type: ActionTypes.STORAGE_EVENT,
    event
  };
};

const _openMessage = (dispatch, message, message_type) => {
  const code = Math.random();
  const growlerDelay = 7000;
  setTimeout(() => dispatch(_closeMessage(code)), growlerDelay);
  dispatch(
    {
      type: ActionTypes.OPEN_MESSAGE,
      code,
      message_type,
      message
    });
};

const _closeMessage = (code) => {
  return { type: ActionTypes.CLOSE_MESSAGE, code };
};

export {
  updateIndicatorTimesAsyncPromise,
  populateIndicatorDetailsAsyncPromise,
  toggleBoundary,
  activateBoundary,
  populateWindMagnitudeDataPromise,
  populateWindDirectionDataPromise,
  setWindVelocityData,
  setWindBarbData,
  populateAwsDataAsync,
  populateAwsDataForGraphAsyncPromise,
  populateAqfxDataAsync,
  populateFmsDataAsync,
  populateFmsDataForGraphAsyncPromise,
  populateManualWeatherAsync,
  populateBurnPrescriptionsAsync,
  setMapBoundsAndZoom,
  setMapCenter,
  mapRecentred
};



// WEBPACK FOOTER //
// ./src/actions/index.js